<template>
  <div
    class="select"
    :class="classes"
  >
    <button
      class="select__trigger outline"
      :style="{ 'anchor-name': `--${dropdownId}` }"
      type="button"
      :popovertarget="!isDisabled ? dropdownId : null"
      @keydown.up.prevent="toggle"
      @keydown.down.prevent="toggle"
    >
      <div class="trigger__container">
        <slot name="leading" />

        <div class="trigger__content">
          <span
            class="content__label"
            v-html="autoLabel"
          />

          <slot name="selected">
            <span
              v-if="!$slots.selected && (model !== null || props.multiSelect)"
              class="content__value"
              v-html="selectedLabel"
            />
          </slot>
        </div>

        <template v-if="showIcon">
          <the-icon
            v-if="isInvalid"
            art="solid"
            class="invalid-icon"
            name="circle-exclamation"
          />
          <the-icon
            v-else-if="$props.hasIconValidIndicator && isValid"
            art="solid"
            class="valid-icon"
            name="circle-check"
          />
        </template>

        <slot name="trailing">
          <template v-if="!props.readonly">
            <the-icon
              class="trigger__indicator trigger__indicator--closed"
              art="solid"
              name="chevron-up"
            />
            <the-icon
              class="trigger__indicator trigger__indicator--open"
              art="solid"
              name="chevron-down"
            />
          </template>
        </slot>
      </div>
    </button>

    <div class="select__hint caption-regular">
      <span v-text="hint" />
    </div>

    <basic-dropdown-menu
      :id="dropdownId"
      ref="dropdownRef"
      :forced-width="$props.forcedWidth"
      :multi-select="$props.multiSelect"
      :name="$props.name"
      :options="$props.options"
      :selected="model"
      :size="$props.size"
      @select="selectItem"
    />
  </div>
</template>

<script setup>
  import { computed, useTemplateRef } from 'vue'
  import { v4 as uuidv4 } from 'uuid'

  import useI18n from '@shared/hooks/useI18n'

  import BasicDropdownMenu from '@shared/components/Basic/DropdownMenu'

  // HOOKS
  const { t } = useI18n()

  // INIT
  const model = defineModel({ type: [String, Object, Number, Array] })
  const props = defineProps({
    disabled: {
      type: Boolean,
      default: false,
    },

    label: {
      type: String,
      default: '',
    },

    manualValidation: {
      type: Boolean,
      default: false,
    },

    multiSelect: {
      type: Boolean,
      default: false,
    },

    name: {
      type: String,
      required: true,
    },

    options: {
      type: Array,
      required: true,
    },

    readonly: {
      type: Boolean,
      default: false,
    },

    size: {
      validator(value) {
        return ['sm', 'md'].includes(value)
      },
      default: 'sm',
    },

    supportingText: {
      type: String,
      default: undefined,
    },

    v: {
      type: Object,
      default: () => {},
    },
  })

  // DATA
  const KEY = uuidv4()
  const dropdownRef = useTemplateRef('dropdownRef')
  const dropdownId = `dropdown-${KEY}`

  // COMPUTED
  const autoLabel = computed(() => {
    let str = props.label
    if (isRequired.value) {
      str += ' *'
    }

    return str
  })

  const classes = computed(() => {
    return {
      [`select--${props.size}`]: true,
      'select--empty': isEmpty.value,
      'select--success': props.hasIconValidIndicator && isValid.value,
      'select--error': isInvalid.value && !isDisabled.value,
      'select--disabled': isDisabled.value,
      'select--multi': props.multiSelect,
    }
  })

  const hint = computed(() => {
    if (isInvalid.value) {
      const error = props.v?.$errors[0]?.$validator
      if (error && !isDisabled.value) {
        return t(`form.error.${error}`)
      }
    }
    return props.supportingText
  })

  const isDisabled = computed(() => {
    return props.disabled || props.readonly
  })

  const isEmpty = computed(() => {
    return model.value === undefined || model.value === null || model.value.length === 0
  })

  const isInvalid = computed(() => {
    return props.v?.$dirty && props.v?.$invalid
  })

  const isRequired = computed(() => {
    return !!props.v?.required
  })

  const isValid = computed(() => {
    return props.v?.$dirty && !props.v?.$invalid
  })

  const selectedLabel = computed(() => {
    if (props.multiSelect) {
      /*
        Sets label to 'all selected' if all options select or 'none selected' if no option is select.
        If one option is selected, show the label of the select option. Otherwise show how many options are selected.
      */
      const selectedOptions = props.options.filter(option => model.value.includes(option.value))
      if (selectedOptions.length === props.options.length) {
        return t('form.multiSelect.allSelected')
      }

      if (selectedOptions.length === 0) {
        return t('form.multiSelect.noneSelected')
      }

      if (selectedOptions.length === 1) {
        return selectedOptions[0].label
      }

      return t('form.multiSelect.someSelected', { quantity: selectedOptions.length })
    }
    return props.options.find(o => o.value === model.value)?.label
  })

  const showIcon = computed(() => {
    return (isInvalid.value && !isDisabled.value) || (props.hasIconValidIndicator && isValid.value)
  })

  // METHODS
  function selectItem(item) {
    if (props.multiSelect) {
      model.value = item
      return
    }

    model.value = item.value
    dropdownRef.value.close()

    if (!props.manualValidation) props.v?.$validate()
  }

  function toggle() {
    if (isDisabled.value) return

    dropdownRef.value.toggle()
  }
</script>

<style>
:root,
.surface-default {
  --dvp-select-color-background: var(--Input-fields-input-on-default-surface-fill);
  --dvp-select-color-active-indicator: var(--on-surface);
  --dvp-select-color-background-disabled: var(--Interaction-States-disabled-default);
  --dvp-select-color-label-disabled: var(--on-surface);
  --dvp-select-c-outline: var(--on-surface);
}

.surface-alternative {
  --dvp-select-color-background: var(--Input-fields-input-on-alternative-surface-fill);
  --dvp-select-color-active-indicator: var(--on-surface);
  --dvp-select-color-background-disabled: var(--Interaction-States-disabled-extended);
  --dvp-select-color-label-disabled: var(--on-surface);
  --dvp-select-c-outline: var(--on-surface);
}

.surface-inverted {
  --dvp-select-color-background: var(--Input-fields-input-on-Inverted-surface-fill);
  --dvp-select-color-active-indicator: var(--Input-fields-input-on_Inverted-surface-active-indicator);
  --dvp-select-color-background-disabled: var(--Interaction-States-disabled-inverse);
  --dvp-select-color-label-disabled: var(--Interaction-States-disabled-inverse-label);
  --dvp-select-c-outline: var(--on-inverse-surface-variant);
}
</style>
<style scoped>
.select {
  --dvp-select-color-label: var(--on-surface-medium);
  --dvp-select-color-container-border: var(--dvp-select-color-active-indicator);
  --dvp-select-color-hint: var(--on-surface-medium);

  display: inline-block;
  width: 100%;
  margin: 0 0 var(--fixed-spacing-fix-04);
  position: relative;

  &--success {
    --dvp-select-color-label: var(--on-surface-medium);
    --dvp-select-color-container-border: var(--secondary);
    --dvp-select-color-hint: var(--on-surface-medium);
  }

  &--error {
    --dvp-select-color-label: var(--error);
    --dvp-select-color-container-border: var(--error);
    --dvp-select-color-hint: var(--error);
  }

  &--disabled {
    cursor: default !important;
    user-select: none;

    .select__trigger {
      cursor: default;
      background-color: var(--dvp-select-color-background-disabled);
      color: var(--dvp-select-color-label-disabled);
    }
  }

  &--sm {
    --dvp-select-font-size-label: var(--font-size-regular-md);
    --dvp-select-letter-spacing-label: var(--letter-spacing-regular-md);
    --dvp-select-line-height-label: var(--line-height-regular-md);
  }

  &--md {
    --dvp-select-font-size-label: var(--font-size-regular-xl);
    --dvp-select-letter-spacing-label: var(--letter-spacing-regular-xl);
    --dvp-select-line-height-label: calc(var(--line-height-regular-xl) - var(--fixed-spacing-fix-01));
  }

  &:deep(.icon) {
    font-size: var(--font-size-regular-md);
    color: var(--on-surface-medium);
  }

  &:has(:popover-open) .trigger__indicator--open {
     display: none;
  }

  &:not(:has(:popover-open)) .trigger__indicator--closed {
    display: none;
  }
}

.select__trigger {
  background-color: var(--dvp-select-color-background);
  border-radius: var(--Input-fields-input-border-radius);
  height: var(--spacing-san-spacing-12);
  padding: 0 var(--fixed-spacing-fix-04);
  cursor: pointer;
  border: none;
  width: 100%;
  text-align: left;

  &.has-outline:focus-visible {
    outline: 2px solid var(--dvp-select-c-outline) !important;
    outline-offset: var(--fixed-spacing-fix-01);
    border-radius: var(--fixed-border-radius-fix-02);
  }
}

.trigger__container {
  display: flex;
  align-items: center;
  gap: var(--fixed-spacing-fix-04);
  height: 100%;
  box-shadow: inset 0 -1px 0 var(--dvp-select-color-container-border);

  white-space: nowrap;
}

.trigger__container .trigger__indicator {
  color: var(--on-surface-medium);
}

.trigger__content {
  display: flex;
  flex-direction: column;
  width: calc(100% - 32px);
  position: relative;
}

.icon + .trigger__content {
  width: calc(100% - 64px);
}

.content__label {
  position: absolute;
  top: -32px;
  width: 100%;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  color: var(--dvp-select-color-label);
}

.select--empty:not(.select--multi) .content__label,
.content__value {
  display: inline-block;
  overflow: hidden;

  padding: var(--line-height-regular-xs) 0 0 0;
  font-size: var(--dvp-select-font-size-label);
  letter-spacing: var(--dvp-select-letter-spacing-label);
  line-height: var(--dvp-select-line-height-label);
  text-overflow: ellipsis;
}

.select:not(.select--empty) .content__label,
.select--multi .content__label {
  top: 0;
  font-size: var(--font-size-regular-xs);
  line-height: var(--line-height-regular-xs);
  letter-spacing: var(--letter-spacing-regular-xs);
}

.valid-icon.icon {
  color: var(--secondary);
}

.invalid-icon.icon {
  color: var(--error);
}

.select__hint {
  --dvp-select-indent-hint: var(--fixed-spacing-fix-04);

  color: var(--dvp-select-color-hint);
  margin: 0 0 0 var(--dvp-select-indent-hint);
  height: var(--line-height-regular-xs);
  display: inline-block;
  width: 100%;

  & span {
    display: inline-block;
    width: calc(100% - (2 * var(--dvp-select-indent-hint)));
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
</style>
